import { useState, useCallback, useRef } from 'react';

const DEFAULT_DURATION_MS = 4500;
const EXIT_ANIMATION_MS = 300;

export const useNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef(new Map());
  const idRef = useRef(0);

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const removeNotification = useCallback(
    (id) => {
      clearTimer(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [clearTimer]
  );

  const showNotification = useCallback(
    (message, type = 'info', duration = DEFAULT_DURATION_MS) => {
      const id = ++idRef.current;
      const safeDuration =
        typeof duration === 'number' && Number.isFinite(duration)
          ? duration
          : DEFAULT_DURATION_MS;

      setNotifications((prev) => [
        ...prev,
        { id, message, type, duration: safeDuration },
      ]);

      // Remove após duração + animação de saída (fonte única de auto-fechamento)
      if (safeDuration > 0) {
        const timer = setTimeout(() => {
          timersRef.current.delete(id);
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, safeDuration + EXIT_ANIMATION_MS);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    []
  );

  const showSuccess = useCallback(
    (message, duration = DEFAULT_DURATION_MS) =>
      showNotification(message, 'success', duration),
    [showNotification]
  );

  const showError = useCallback(
    (message, duration = DEFAULT_DURATION_MS) =>
      showNotification(message, 'error', duration),
    [showNotification]
  );

  const showWarning = useCallback(
    (message, duration = DEFAULT_DURATION_MS) =>
      showNotification(message, 'warning', duration),
    [showNotification]
  );

  const showInfo = useCallback(
    (message, duration = DEFAULT_DURATION_MS) =>
      showNotification(message, 'info', duration),
    [showNotification]
  );

  return {
    notifications,
    showNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
